import { ValidationIssue } from '../../services/deliveryConfigApi';
import {
  PromoAudience,
  PromoCodeCreateRequest,
  PromoCodeDetail,
  PromoCodeUpdateRequest,
  PromoDiscountType,
} from '../../types/promotions';

/** Raw form state — numbers stay strings until validated so a half-typed value is not coerced. */
export interface PromoFormValues {
  code: string;
  name: string;
  audience: PromoAudience;
  discountType: PromoDiscountType;
  discountValue: string;
  maxDiscountPerUse: string;
  usesPerHolder: string;
  maxHolders: string;
  /** `datetime-local` value; '' = now. */
  startsAt: string;
  /** `datetime-local` value; ignored when `noExpiry`. */
  expiresAt: string;
  noExpiry: boolean;
}

export const CODE_RE = /^[A-Za-z0-9]{4,20}$/;
export const EXPIRY_PRESETS = [7, 30, 90] as const;

const pad = (n: number) => String(n).padStart(2, '0');

/** ISO instant → `YYYY-MM-DDTHH:mm` in the browser's zone (what `datetime-local` shows). */
export function toLocalInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** `datetime-local` value → ISO instant; '' → null. */
export function fromLocalInput(local: string): string | null {
  if (!local) return null;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** Expiry preset: start (or now) + N days, as a `datetime-local` value. */
export function presetExpiry(startsAt: string, days: number, now: Date = new Date()): string {
  const base = startsAt ? new Date(startsAt) : now;
  const d = new Date(base.getTime() + days * 24 * 60 * 60 * 1000);
  return toLocalInput(d.toISOString());
}

export function initialValues(promo?: PromoCodeDetail | null): PromoFormValues {
  if (!promo) {
    return {
      code: '', name: '', audience: 'PUBLIC', discountType: 'PERCENT', discountValue: '', maxDiscountPerUse: '',
      usesPerHolder: '1', maxHolders: '', startsAt: '', expiresAt: '', noExpiry: false,
    };
  }
  return {
    code: promo.code,
    name: promo.name,
    audience: promo.audience,
    discountType: promo.discountType,
    discountValue: String(promo.discountValue),
    maxDiscountPerUse: promo.maxDiscountPerUse == null ? '' : String(promo.maxDiscountPerUse),
    usesPerHolder: String(promo.usesPerHolder),
    maxHolders: String(promo.maxHolders),
    startsAt: toLocalInput(promo.startsAt),
    expiresAt: toLocalInput(promo.expiresAt),
    noExpiry: promo.expiresAt == null,
  };
}

const num = (raw: string): number | null => {
  const t = raw.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
};

const isWhole = (n: number | null): n is number => n !== null && Number.isInteger(n);

interface ValidateContext {
  /** Edit mode carries the loaded code (increase-only limits, locked terms). */
  original?: PromoCodeDetail | null;
  /** Global max discount %; null when the settings could not be read (server still enforces). */
  capPercent: number | null;
  now?: Date;
}

/** Client-side mirror of BR-1..BR-7 and BR-9; the server re-validates everything. */
export function validatePromo(v: PromoFormValues, ctx: ValidateContext): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const add = (field: string, message: string) => issues.push({ field, message });
  const original = ctx.original ?? null;
  const termsEditable = !original?.termsLocked;

  if (!original && !CODE_RE.test(v.code.trim())) add('code', 'Code must be 4–20 letters or digits.');
  if (!v.name.trim()) add('name', 'Enter an internal name.');
  else if (v.name.trim().length > 80) add('name', 'Name must be 80 characters or fewer.');

  if (termsEditable) {
    const value = num(v.discountValue);
    if (v.discountType === 'PERCENT') {
      if (!isWhole(value) || value < 1) add('discountValue', 'Enter a whole percentage of at least 1%.');
      else if (ctx.capPercent !== null && value > ctx.capPercent) {
        add('discountValue', `Percentage cannot exceed the maximum discount (${ctx.capPercent}%).`);
      }
      const max = num(v.maxDiscountPerUse);
      if (v.maxDiscountPerUse.trim() !== '' && (max === null || max < 1)) {
        add('maxDiscountPerUse', 'Max per use must be at least AED 1.');
      }
    } else if (value === null || value < 1 || value > 10000) {
      add('discountValue', 'Enter an amount between AED 1 and AED 10,000.');
    }
  }

  const uses = num(v.usesPerHolder);
  if (!isWhole(uses) || uses < 1 || uses > 100) add('usesPerHolder', 'Uses per customer must be between 1 and 100.');
  else if (original && uses < original.usesPerHolder) add('usesPerHolder', `Uses per customer can only go up (currently ${original.usesPerHolder}).`);

  const holders = num(v.maxHolders);
  if (!isWhole(holders) || holders < 1 || holders > 100000) add('maxHolders', 'Enter the number of users (1–100,000).');
  else if (original && holders < original.holdersCount) add('maxHolders', `Can't go below current usage (${original.holdersCount}).`);
  else if (original && holders < original.maxHolders) add('maxHolders', `Number of users can only go up (currently ${original.maxHolders}).`);

  if (!v.noExpiry) {
    const now = ctx.now ?? new Date();
    const expires = v.expiresAt ? new Date(v.expiresAt) : null;
    const start = v.startsAt ? new Date(v.startsAt) : now;
    const expiryChanged = !original || toLocalInput(original.expiresAt) !== v.expiresAt;
    if (!expires || Number.isNaN(expires.getTime())) add('expiresAt', 'Pick an expiry date, or choose "No expiry".');
    else if (expires <= start) add('expiresAt', 'Expiry must be after the start.');
    else if (expiryChanged && expires <= now) add('expiresAt', "Expiry can't be in the past.");
  }
  return issues;
}

function money(raw: string): number | null {
  const n = num(raw);
  return n === null ? null : Math.round(n * 100) / 100;
}

export function toCreateRequest(v: PromoFormValues): PromoCodeCreateRequest {
  const percent = v.discountType === 'PERCENT';
  return {
    code: v.code.trim().toUpperCase(),
    name: v.name.trim(),
    audience: v.audience,
    discountType: v.discountType,
    discountValue: money(v.discountValue) as number,
    maxDiscountPerUse: percent ? money(v.maxDiscountPerUse) : null,
    usesPerHolder: Number(v.usesPerHolder),
    maxHolders: Number(v.maxHolders),
    startsAt: fromLocalInput(v.startsAt),
    expiresAt: v.noExpiry ? null : fromLocalInput(v.expiresAt),
  };
}

/** Locked fields ride only while the terms are still editable (BR-8). */
export function toUpdateRequest(v: PromoFormValues, original: PromoCodeDetail): PromoCodeUpdateRequest {
  const body: PromoCodeUpdateRequest = {
    version: original.version,
    name: v.name.trim(),
    audience: v.audience,
    expiresAt: v.noExpiry ? null : fromLocalInput(v.expiresAt),
    maxHolders: Number(v.maxHolders),
    usesPerHolder: Number(v.usesPerHolder),
  };
  if (!original.termsLocked) {
    const create = toCreateRequest(v);
    body.discountType = create.discountType;
    body.discountValue = create.discountValue;
    body.maxDiscountPerUse = create.maxDiscountPerUse;
    body.startsAt = create.startsAt;
  }
  return body;
}
